<template>
  <div ref="selectContainer" :class="['cs__container', { 'active': isDropdownActive }]">
    <button ref="selectInput" class="cs__select" @click="toggleDropdown" role="combobox" aria-controls="listbox"
      aria-haspopup="listbox" :aria-expanded="isDropdownActive.toString()">
      <div v-if="selectInput?.value " class="cs__select-selected-value">
        <div v-if="isColorOption" class="cs__option-color">
          <ImageField v-if="selectedColorOption.image" :key="selectedColorOption.image" :obj="{'url':selectedColorOption.image,'alt': selectedColorOption.name,'width':160,'height':160}"/>
          <span class="cs__option-name">{{selectInput.value}}</span>
        </div>
        <span v-else>{{ isSizeOption ? formatSizeOption(selectedOption) :  selectInput.value }}</span>
      </div>
      <span v-else class="cs__select-placeholder">{{ placeholder }}</span>
    </button>
    <div v-if="isDropdownActive" class="cs__options" role="listbox" id="listbox">
      <div v-if="windowWidth < 768" class="cs__option-placeholder">
        <span>Select Your Distance</span>
        <svg @click="toggleDropdown" class="cs__icon-close">
          <use xlink:href="#close"></use>
        </svg>
      </div>
      <template v-if="!isColorOption">
        <div v-for="option in options" :key="option" :class="['cs__option' , {'selected': selectedOption === option}]" @click="changeOption(option)" @keydown="handleKeydownOnOptions($event, option)" role="option" tabindex="0" :aria-selected="selectedOption === option ? true : false">
          {{ enablePrefixOnOption ? optionPrefix : null }} {{ isSizeOption ? formatSizeOption(option) :  option }} {{ enableSuffixOnOption ? optionSuffix : null }}
        </div>
      </template>
      <template v-else>
        <div v-for="(value,name,index) in options" :key="index" :class="['cs__option cs__option-color' , {'selected': selectedOption === name}]" @click="changeOption(name)" @keydown="handleKeydownOnOptions($event, name)" role="option" tabindex="0" :aria-selected="selectedOption === name ? true : false">
          <ImageField v-if="value.image" :key="value.image" :obj="{'url':value.image,'alt': name,'width':160,'height':160}"/>
          <span class="cs__option-name">{{name}}</span>
        </div>
      </template>
    </div>
    <svg class="cs__icon">
      <use xlink:href="#icon-chevron-down"></use>
    </svg>
  </div>
</template>

<script setup>
  import { toRefs, ref, onMounted, onUnmounted, computed } from 'vue';
  import ImageField from "./image.vue";

  const props = defineProps({
    options: {
      type: Array,
      required: true,
    },

    optionPrefix: {
      type: String,
      default: ''
    },

    optionSuffix: {
      type: String,
      default: ''
    },

    placeholder: {
      type: String,
      default: 'Select a option'
    },

    callbackProps: {
      type: Object,
      default: {}
    },

    onDropdownSelect: {
      type: Function,
    },

    enableBodyOverflow: {
      type: Boolean,
      default: true
    },

    enablePrefixOnOption: {
      type: Boolean,
      default: true
    },

    enableSuffixOnOption: {
      type: Boolean,
      default: true
    },

    selectedValueProp: {
      type: String,
      default: ''
    },

    isColorOption: {
      type: Boolean,
      default: false
    },

    isSizeOption: {
      type: Boolean,
      default: false
    }
  })

  const { options, optionPrefix, optionSuffix, callbackProps, enableBodyOverflow, selectedValueProp, isColorOption } = toRefs(props)
  const selectContainer = ref(null)
  const selectInput = ref(null)
  const selectedOption = ref(null)
  const isDropdownActive = ref(false)
  const overlay = new window.Overlay();
  var windowWidth = ref(window.innerWidth)

  onMounted(() => {
    window.addEventListener('resize', () => {
      windowWidth.value = window.innerWidth
    })

    document.addEventListener('click', handleClickOutside);

    if (selectedValueProp.value) {
      selectInput.value.value = optionPrefix.value + ' ' + selectedValueProp.value + ' ' + optionSuffix.value;
      selectedOption.value = selectedValueProp.value;
    }
  });

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
  });

  defineExpose({ selectedOption })

  const selectedColorOption = computed(() => {
    if(!isColorOption.value) return

    return Object.values(options.value).find(option => option.name === selectedOption.value)
  })

  const formatSizeOption = (size) => {
    return window.formattedSize(size)
  }

  const toggleDropdown = () => {
    isDropdownActive.value = !isDropdownActive.value;
    let bodyStyle = document.body.style;

    if (enableBodyOverflow.value) {
      if (windowWidth.value < 768) {
        if (isDropdownActive.value) {
          overlay.show();
          bodyStyle.overflow = 'hidden'
        } else {
          overlay.hide();
          bodyStyle.overflow = 'auto'
        }
      } else {
        overlay.hide();
        bodyStyle.overflow = 'auto'
      }
    }
  }

  overlay.on('cancel', () => {
    toggleDropdown()
  });

  const changeOption = (option) => {
    selectInput.value.value = optionPrefix.value + ' ' + option + ' ' + optionSuffix.value;
    selectedOption.value = option;

    if (props.onDropdownSelect) {
      props.onDropdownSelect(option, callbackProps.value);
    }

    toggleDropdown()
  }

  const handleClickOutside = (event) => {
    if (isDropdownActive.value && selectContainer.value && !selectContainer.value.contains(event.target)) {
      toggleDropdown();
    }
  };

  const handleKeydownOnOptions = (event, selOption) => {
    if (event.key === 'Escape') {
      toggleDropdown();
    } else if (event.key === 'Enter' || event.key === ' ') {
      changeOption(selOption);
      event.preventDefault(); // Prevent scrolling with Space key
    }
  };
</script>